<template>
  <asc-container
    v-if="!isSupportInfoLoading && showSupportingInfo && !isCustomAlert"
    class="asc-mx-rec-support-info--wrapper"
    flat
  >
    <template slot="header"
      ><h4 class="asc-mx-rec-support-info--wrapper-header">
        SUPPORTING INFORMATION
      </h4></template
    >
    <template
      v-if="
        isEditPermissionInvestigate &&
        !isRecommendationTab &&
        showSupportingText &&
        !isAirlineTier3OrTier4
      "
      slot="subheader"
    >
      <p class="asc-mx-rec-support-info--wrapper-subheader">
        Select the eye icon(s) to add and remove table items to Mx Rec.
      </p>
    </template>
    <asc-accordion
      ref="ascMxRecSupportInfoAccordion"
      :value="getAccordionItemToExpand"
      :multiple="multiple"
      class="asc-mx-rec-support-info--accordion"
    >
      <!-- ATTACHMENTS --->
      <asc-accordion-item
        v-if="showAttachments"
        id="ATTACHMENTS"
        class="asc-mx-rec-support-info--accordion-item"
      >
        <template slot="header">ATTACHMENTS</template>
        <template
          v-if="
            showSupportingText &&
            !(isEditPermissionInvestigate && !isRecommendationTab) &&
            isAirlineTier3OrTier4
          "
          slot="supportingText"
        >
        </template>
        <asc-mx-rec-support-info-attachments-wrapper
          file-upload
          :is-recommendation-tab="isRecommendationTab"
          :disabled="disabled"
          :is-editable="
            isEditPermissionInvestigate &&
            !isRecommendationTab &&
            isAirlineTier3OrTier4
          "
          :type="getSelectionType('ATTACHMENTS')"
          :attachments-data="attachmentsData"
          :airline-tier-type-id="airlineTierTypeId"
          @onCaptionUpdate="handleUpdateCaption"
          @onAttachmentCaptionUpdate="handleUpdateAttachmentCaption"
          @onDownloadFile="handleFileDownload"
          @onFileSelected="handleFileSelected"
          @onFileUploaded="handleFileUpload"
          @onVisibilityReminder="handleVisibilityReminder"
          @onVisibilityConfirmation="handleConfirmingVisibility"
        ></asc-mx-rec-support-info-attachments-wrapper>
      </asc-accordion-item>

      <!-- PLOTS --->
      <asc-accordion-item
        v-if="showPlots"
        id="PLOTS"
        class="asc-mx-rec-support-info--accordion-item"
      >
        <template slot="header">PLOTS </template>
        <template
          v-if="
            showSupportingText &&
            !(isEditPermissionInvestigate && !isRecommendationTab) &&
            !isAirlineTier3OrTier4
          "
          slot="supportingText"
        >
          <span :class="{ disabled: disabled }">
            <template v-if="isPlotDataLoading">
              <v-progress-circular
                :size="20"
                :width="3"
                indeterminate
              ></v-progress-circular>
            </template>
          </span>
        </template>
        <asc-mx-rec-support-info-attachments-wrapper
          :is-recommendation-tab="isRecommendationTab"
          :disabled="disabled"
          :is-editable="isEditPermissionInvestigate && !isRecommendationTab"
          title="Plots"
          :selections="getVisiblePlots"
          :page-plots="pagePlots"
          :are-plots-editable="arePlotsEditable && !isAirlineTier3OrTier4"
          :loading-plot-ids="loadingPlotIds"
          :chart-data="chartData"
          :handle-load-plot="onApplyPagePlot"
          :attachments-caption="getSupportAttachmentsCaption"
          :hide-hidden-tabs="hideHiddenTabs"
          :on-toggle-page-plot-visibility="onTogglePagePlotVisibility"
          :on-update-page-plot-caption="onUpdatePagePlotCaption"
          :on-expand="onExpand"
          :on-add-tab-to-page-plot="onAddTabToPagePlot"
          :on-remove-tab-from-page-plot="onRemoveTabFromPagePlot"
          :on-update-tab-view-page-plot="onUpdateTabViewPagePlot"
          :airline-tier-type-id="airlineTierTypeId"
          @onVisibilityReminder="handleVisibilityReminder"
          @onVisibilityConfirmation="handleConfirmingVisibility"
          @onRemovePlot="handleRemovePlot"
          @onPlotVisibility="handlePlotVisibility"
        ></asc-mx-rec-support-info-attachments-wrapper>
      </asc-accordion-item>

      <!-- PHM ALERTS -->
      <asc-accordion-item
        v-if="showPHMAlerts"
        id="PHM_ALERTS"
        class="asc-mx-rec-support-info--accordion-item"
      >
        <template slot="header">{{ phmAlertsData.sectionTitle }}</template>
        <template
          v-if="
            showSupportingText &&
            !(isEditPermissionInvestigate && !isRecommendationTab) &&
            !isAirlineTier3OrTier4
          "
          slot="supportingText"
        >
        </template>
        <asc-mx-rec-support-info-grid-wrapper
          :is-recommendation-tab="isRecommendationTab"
          :show-eyeball-column="isEyeBallVisible && !isAirlineTier3OrTier4"
          :is-editable="isEditable"
          :disabled="disabled"
          :read-only-eyeball-column="
            isReadOnlyPermissionInvestigate && !isRecommendationTab
          "
          :type="getSelectionType('PHM_ALERTS')"
          :data="phmAlertsData"
          :airline-tier-type-id="airlineTierTypeId"
          @onCaptionUpdate="handleUpdateCaption"
          @onRowSelected="handleSupportInfoRowSelected"
          @onVisibilityReminder="handleVisibilityReminder"
          @onVisibilityConfirmation="handleConfirmingVisibility"
        />
      </asc-accordion-item>

      <!-- FAULT MESSAGES -->
      <asc-accordion-item
        v-if="showFaultMessages && !isAirlineTier3OrTier4"
        id="FAULT_MESSAGES"
        class="asc-mx-rec-support-info--accordion-item"
      >
        <template slot="header">{{ faultMessagesData.sectionTitle }}</template>
        <template v-if="showSupportingText" slot="supportingText">
          <span :class="{ disabled: disabled }">
            ({{ selectedFaultMessages }} Viewable to customer in Mx Rec)
          </span>
        </template>
        <asc-mx-rec-support-info-grid-wrapper
          :is-recommendation-tab="isRecommendationTab"
          :show-eyeball-column="isEyeBallVisible"
          :is-editable="isEditable"
          :disabled="disabled"
          :read-only-eyeball-column="
            isReadOnlyPermissionInvestigate && !isRecommendationTab
          "
          :type="getSelectionType('FAULT_MESSAGES')"
          :data="faultMessagesData"
          :airline-tier-type-id="airlineTierTypeId"
          @onCaptionUpdate="handleUpdateCaption"
          @onRowSelected="handleSupportInfoRowSelected"
          @onVisibilityReminder="handleVisibilityReminder"
          @onVisibilityConfirmation="handleConfirmingVisibility"
        />
      </asc-accordion-item>

      <!-- REMOVALS -->
      <asc-accordion-item
        v-if="
          showRemovals &&
          !isAirlineTier3OrTier4 &&
          !(isEditPermissionInvestigate && !isRecommendationTab)
        "
        id="REMOVALS"
        class="asc-mx-rec-support-info--accordion-item"
      >
        <template slot="header">{{ removalsData.sectionTitle }}</template>
        <asc-mx-rec-support-info-grid-wrapper
          :is-recommendation-tab="isRecommendationTab"
          :show-eyeball-column="false"
          :is-editable="isEditable"
          :disabled="disabled"
          :read-only-eyeball-column="
            isReadOnlyPermissionInvestigate && !isRecommendationTab
          "
          :type="getSelectionType('REMOVALS')"
          :data="removalsData"
          :has-caption-option="false"
          @onCaptionUpdate="handleUpdateCaption"
          @onRowSelected="handleSupportInfoRowSelected"
          @onVisibilityReminder="handleVisibilityReminder"
          @onVisibilityConfirmation="handleConfirmingVisibility"
        />
      </asc-accordion-item>
    </asc-accordion>
  </asc-container>
</template>

<script>
import AscAccordion from '../atoms/AscAccordion.vue'
import AscAccordionItem from '../atoms/AscAccordionItem.vue'
import AscContainer from '../atoms/AscContainer.vue'

import AscMxRecSupportInfoGridWrapper from '../organisms/AscMxRecSupportInfoGridWrapper.vue'
import AscMxRecSupportInfoAttachmentsWrapper from '../organisms/AscMxRecSupportInfoAttachmentsWrapper.vue'

import {
  AscMxRecPropsMixin,
  AscMxRecPermissionMixin,
} from '@/components/mixins'

import { COLLINS_PHM_TIER_3, COLLINS_PHM_TIER_4 } from '@/utilities/appConsts'

export default {
  name: 'AscMxRecSupportInfoWrapper',

  components: {
    AscContainer,
    AscAccordion,
    AscAccordionItem,
    AscMxRecSupportInfoAttachmentsWrapper,

    AscMxRecSupportInfoGridWrapper,
  },

  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],

  props: {
    // Shared
    isSupportInfoLoading: {
      type: Boolean,
      default: false,
    },
    supportingInfoSelectionTypes: {
      type: Array,
      default: () => [],
      required: true,
    },
    accordionItemToExpand: {
      type: Array,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isCustomAlert: {
      type: Boolean,
      default: false,
    },
    // Attachments
    attachmentsData: {
      type: Object,
      default: () => ({
        attachments: [],
        attachmentsCaption: '',
        selectedFiles: [],
        fileTypeProperties: {},
        attachmentUploadError: [],
      }),
      required: true,
    },

    // Plots
    isPlotDataLoading: {
      type: Boolean,
      default: false,
    },
    pagePlots: {
      type: Array,
      default: () => [],
    },
    arePlotsEditable: {
      type: Boolean,
      default: false,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    loadingPlotIds: {
      type: Array,
      default: () => [],
    },
    onApplyPagePlot: {
      type: Function,
      default: () => {},
    },

    hideHiddenTabs: {
      type: Boolean,
      default: true,
    },
    onTogglePagePlotVisibility: {
      type: Function,
      default: () => {},
    },
    onUpdatePagePlotCaption: {
      type: Function,
      default: () => {},
    },
    onExpand: {
      type: Function,
      default: () => {},
    },
    onAddTabToPagePlot: {
      type: Function,
      default: () => {},
    },
    onRemoveTabFromPagePlot: {
      type: Function,
      default: () => {},
    },
    onUpdateTabViewPagePlot: {
      type: Function,
      default: () => {},
    },

    // PHM Alerts
    phmAlertsData: {
      type: Object,
      default: () => ({
        caption: '',
        grids: [],
        sectionTitle: '',
        selectedRows: [],
      }),
      required: true,
    },

    // Fault Messages
    faultMessagesData: {
      type: Object,
      default: () => ({
        caption: '',
        grids: [],
        sectionTitle: '',
        selectedRows: [],
      }),
      required: true,
    },

    // Removals
    removalsData: {
      type: Object,
      default: () => ({
        caption: '',
        grids: [],
        sectionTitle: '',
        selectedRows: [],
      }),
      required: true,
    },
  },

  data() {
    return {
      accordionPanel: [],
    }
  },

  computed: {
    // Shared
    isEditable() {
      return (
        ((this.isEditPermissionInvestigate ||
          this.isReadOnlyPermissionInvestigate) &&
          !this.isRecommendationTab) ||
        this.airlineTierTypeId === COLLINS_PHM_TIER_3
      )
    },
    isEyeBallVisible() {
      return this.isEditable && this.airlineTierTypeId !== COLLINS_PHM_TIER_3
    },
    showSupportingText() {
      return (
        !this.isRecommendationTab &&
        this.airlineTierTypeId !== COLLINS_PHM_TIER_3
      )
    },
    showSupportingInfo() {
      return (
        !this.isRecommendationTab ||
        this.showAttachments ||
        this.showPlots ||
        this.showPHMAlerts ||
        this.showFaultMessages ||
        this.showRemovals
      )
    },
    getAccordionItemToExpand() {
      if (this.accordionPanel.length) {
        const accordionRef = this.$refs.ascMxRecSupportInfoAccordion
        const availableAccordionItems = []
        if (accordionRef) {
          const ascAccordionGroup = accordionRef.$refs.ascAccordionGroup
          const ascAccordionItems = ascAccordionGroup.$children
          for (let i = 0; i < ascAccordionItems.length; i++) {
            if (this.accordionPanel.indexOf(ascAccordionItems[i].id) > -1) {
              availableAccordionItems.push(i)
            }
          }
        }

        return availableAccordionItems
      }

      return []
    },

    // Attachments
    getSelectedAttachments() {
      return this.attachmentsData.selectedFiles.length
    },
    showAttachments() {
      return (
        (this.isRecommendationTab && this.getSelectedAttachments > 0) ||
        !this.isRecommendationTab ||
        (this.attachmentsData.attachments.length &&
          this.airlineTierTypeId === COLLINS_PHM_TIER_3)
      )
    },

    // Plots
    getVisiblePlots() {
      return this.pagePlots.filter(item => {
        return item.plotVisible
      })
    },

    getVisiblePlotsCount() {
      return this.getVisiblePlots.length
    },

    getIfAnyPlotsAreVisible() {
      return this.getVisiblePlots.length > 0
    },

    showPlots() {
      return (
        (this.isRecommendationTab && this.getIfAnyPlotsAreVisible) ||
        !this.isRecommendationTab ||
        (this.pagePlots.length && this.airlineTierTypeId === COLLINS_PHM_TIER_3)
      )
    },

    getSupportAttachmentsCaption() {
      return this.aircraftDetails?.attachmentsCaption
    },

    // PHM Alerts
    selectedPHMAlerts() {
      return this.phmAlertsData.selectedRows
        ? this.phmAlertsData.selectedRows.length
        : 0
    },
    showPHMAlerts() {
      return (
        this.phmAlertsData.grids &&
        this.phmAlertsData.grids.length &&
        ((this.isRecommendationTab && this.selectedPHMAlerts > 0) ||
          !this.isRecommendationTab ||
          this.airlineTierTypeId === COLLINS_PHM_TIER_3)
      )
    },

    // Fault Messages
    selectedFaultMessages() {
      return this.faultMessagesData.selectedRows
        ? this.faultMessagesData.selectedRows.length
        : 0
    },
    showFaultMessages() {
      return (
        this.faultMessagesData.grids &&
        this.faultMessagesData.grids.length &&
        ((this.isRecommendationTab && this.selectedFaultMessages > 0) ||
          !this.isRecommendationTab ||
          this.airlineTierTypeId === COLLINS_PHM_TIER_3)
      )
    },

    // Removals
    selectedRemovals() {
      return this.removalsData.selectedRows
        ? this.removalsData.selectedRows.length
        : 0
    },
    showRemovals() {
      return (
        this.removalsData.grids &&
        this.removalsData.grids.length &&
        ((this.isRecommendationTab && this.selectedRemovals > 0) ||
          !this.isRecommendationTab)
      )
    },
    isAirlineTier3OrTier4() {
      return this.airlineTierTypeId === COLLINS_PHM_TIER_3 || COLLINS_PHM_TIER_4
        ? true
        : false
    },
  },

  watch: {
    pagePlots: {
      deep: true,
      handler(pagePlots) {
        this.pagePlots = pagePlots
      },
    },
    accordionItemToExpand(newValue) {
      this.accordionPanel = newValue
    },
    isRecommendationTab() {
      // On toggle of tabs collapse all accordions for performance
      // when attachments have plots it takes long time to switch between tabs
      this.accordionPanel = []
    },
  },

  methods: {
    // Shared
    handleUpdateCaption(updatedCaption) {
      this.$emit('onCaptionUpdate', updatedCaption)
    },
    handleVisibilityReminder(sectionTitle) {
      this.$emit('onVisibilityReminder', sectionTitle)
    },
    handleConfirmingVisibility(sectionTitle) {
      this.$emit('onVisibilityConfirmation', sectionTitle)
    },
    getSelectionType(whichType) {
      const type = this.supportingInfoSelectionTypes?.filter(typeObj => {
        return typeObj.name === whichType
      })
      return type.length === 0 ? {} : type[0]
    },
    handleSupportInfoRowSelected(rowInfo) {
      this.$emit('onRowSelected', rowInfo)
    },
    // Attachments
    handleFileDownload(file) {
      this.$emit('onDownloadFile', file)
    },
    handleFileSelected(file) {
      this.$emit('onFileSelected', file)
    },
    handlePlotVisibility(plotOptions) {
      this.$emit('onPlotVisibility', plotOptions)
    },
    handleFileUpload(file) {
      this.$emit('onFileUploaded', file)
    },
    handleRemovePlot(id) {
      this.$emit('onRemovePlot', id)
    },
    handleUpdateAttachmentCaption(value) {
      this.$emit('onAttachmentCaptionUpdate', value)
    },
  },
}
</script>

<style lang="scss">
.asc-mx-rec-support-info--wrapper {
  &-header {
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 1px;
    line-height: 18px;
    color: $primary-black;
    margin-bottom: 14px;
    text-transform: uppercase;
  }

  &-subheader {
    margin-bottom: 27px !important;
    color: $gray-1000;
  }

  .asc-mx-rec-support-info--accordion {
    &-item {
      .supportingText {
        .disabled {
          color: $gray-600;
        }
      }
    }
  }
}
</style>
